import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getVolatilityDetail } from '../api/metrics';
import { getDefaultDateRange } from '../utils/dateRangeDefaults';
import { useDateRange, useDateRangeStore } from '../stores/useDateRangeStore';
import { formatDollarAmount } from '../utils/currencyFormatter';
import StandaloneSection from './shared/StandaloneSection';
import GranularityDateRangeControls, { TIMEFRAMES, TimeframeKey } from './shared/GranularityDateRangeControls';
import './VolatilityDetailPanel.css';

interface VolatilityDetailPanelProps {
  productId: string;
}


const VolatilityDetailPanel: React.FC<VolatilityDetailPanelProps> = ({ productId }) => {
  // Use same timeframe pattern as ChartView - default to 15m
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeKey>('15m');

  // Memoize granularity to prevent unnecessary recalculations
  const granularity = useMemo(() => TIMEFRAMES[selectedTimeframe].granularityMins, [selectedTimeframe]);

  // Use the shared date range store (same as Profit Monitor)
  const { dateRange } = useDateRange();
  const { setCustomRange } = useDateRangeStore();

  // When granularity changes, update date range to match the new default
  // User can override these afterward using the DateRangeSelector
  const handleTimeframeChange = (newTimeframe: TimeframeKey) => {
    setSelectedTimeframe(newTimeframe);
    const newGranularity = TIMEFRAMES[newTimeframe].granularityMins;
    const range = getDefaultDateRange(newGranularity);
    // Update the date range store with the new default dates
    setCustomRange({
      start_date: range.startTime.split('T')[0],
      end_date: range.endTime.split('T')[0]
    });
  };

  // Initialize date range on mount based on default granularity
  useEffect(() => {
    const range = getDefaultDateRange(granularity);
    setCustomRange({
      start_date: range.startTime.split('T')[0],
      end_date: range.endTime.split('T')[0]
    });
  }, []); // Only run on mount

  // Use dates from the store
  const startDate = dateRange.start_date;
  const endDate = dateRange.end_date;

  // Fetch volatility detail data
  const { data, isLoading, error, isError } = useQuery({
    queryKey: ['volatilityDetail', productId, startDate, endDate, granularity],
    queryFn: () => getVolatilityDetail(productId, startDate, endDate, granularity),
    staleTime: 60000, // 1 minute
    retry: 2,
  });

  // Format text values to title case for better display
  const formatTextValue = (text: string | null | undefined) => {
    if (!text || text === 'N/A') return 'N/A';
    // Convert SCREAMING_SNAKE_CASE or ALLCAPS to Title Case
    return text
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Format volatility state for display
  const formatVolatilityState = (state: string | null | undefined) => {
    if (!state) {
      return <span className="volatility-badge volatility-unknown">UNKNOWN</span>;
    }
    const className = `volatility-badge volatility-${state.toLowerCase()}`;
    return <span className={className}>{state}</span>;
  };

  // Format percentile
  const formatPercentile = (percentile: number | null | undefined) => {
    if (percentile === null || percentile === undefined) {
      return <span className="percentile-low">N/A</span>;
    }
    const className = percentile >= 75 ? 'percentile-high' : percentile >= 50 ? 'percentile-medium' : 'percentile-low';
    return <span className={className}>{percentile}%</span>;
  };

  return (
    <div className="volatility-detail-panel">
      {/* Controls - Always Visible */}
      <GranularityDateRangeControls
        selectedTimeframe={selectedTimeframe}
        onTimeframeChange={handleTimeframeChange}
        isLoading={isLoading}
      />

      {/* Loading State */}
      {isLoading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading volatility analysis...</p>
        </div>
      )}

      {/* Error State */}
      {!isLoading && isError && (
        <StandaloneSection title="ERROR">
          <div className="error-state">
            <p className="error-message">
              Failed to load volatility data: {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        </StandaloneSection>
      )}

      {/* No Data Available */}
      {!isLoading && !isError && data && !data.current_metrics && (!data.volatility_history || data.volatility_history.length === 0) && (
        <StandaloneSection title="NO DATA AVAILABLE">
          <div className="error-state">
            <p className="error-message">
              No volatility data available for the selected configuration.
            </p>
            <div className="error-suggestions">
              <p><strong>Try the following:</strong></p>
              <ul>
                <li>Select a different timeframe</li>
                <li>Wait a few minutes for data collection to complete</li>
                <li>Check that the asset is actively traded</li>
              </ul>
              <p className="error-note">
                <em>Note: Market context data is collected at multiple intervals (1m, 5m, 15m, 30m, 1h, 4h, daily).
                If you're seeing this message, the system may still be collecting initial data for this timeframe.</em>
              </p>
            </div>
          </div>
        </StandaloneSection>
      )}

      {/* Data Content */}
      {!isLoading && !isError && data && (data.current_metrics || (data.volatility_history && data.volatility_history.length > 0)) && (
        <>

      {/* Current Metrics */}
      <StandaloneSection title="CURRENT VOLATILITY METRICS">
        <div className="current-metrics-grid">
          <div className="metric-card">
            <div className="metric-label">Volatility State</div>
            <div className="metric-value">{formatVolatilityState(data.current_metrics?.volatility_state)}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Percentile</div>
            <div className="metric-value">{formatPercentile(data.current_metrics?.volatility_percentile)}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Trend</div>
            <div className="metric-value trend-value">{formatTextValue(data.current_metrics?.trend)}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Momentum</div>
            <div className="metric-value momentum-value">{formatTextValue(data.current_metrics?.momentum_direction)}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Market Regime</div>
            <div className="metric-value regime-value">{formatTextValue(data.current_metrics?.market_regime)}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Last Updated</div>
            <div className="metric-value timestamp-value">
              {data.current_metrics?.analysis_time ? new Date(data.current_metrics.analysis_time).toLocaleString() : 'N/A'}
            </div>
          </div>
        </div>
      </StandaloneSection>

      {/* ATR Analysis */}
      {data.atr_history && data.atr_history.length > 0 && (
        <StandaloneSection title="ATR ANALYSIS (AVERAGE TRUE RANGE)">
          <div className="atr-analysis">
            {/* ATR Summary Cards */}
            <div className="atr-metrics-grid">
              <div className="metric-card">
                <div className="metric-label">Current ATR</div>
                <div className="metric-value atr-value">
                  ${data.atr_history[0]?.atr_value ? formatDollarAmount(data.atr_history[0].atr_value, productId) : 'N/A'}
                </div>
                <div className="metric-sublabel">Latest value</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Average ATR</div>
                <div className="metric-value atr-value">
                  ${formatDollarAmount(data.atr_history.reduce((sum, point) => sum + (point.atr_value || 0), 0) / data.atr_history.length, productId)}
                </div>
                <div className="metric-sublabel">Over date range</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">ATR Range</div>
                <div className="metric-value atr-value">
                  ${formatDollarAmount(Math.min(...data.atr_history.map(p => p.atr_value)), productId)} - ${formatDollarAmount(Math.max(...data.atr_history.map(p => p.atr_value)), productId)}
                </div>
                <div className="metric-sublabel">Min - Max</div>
              </div>
              <div className="metric-card">
                <div className="metric-label">Data Points</div>
                <div className="metric-value">{data.atr_history.length}</div>
                <div className="metric-sublabel">ATR readings</div>
              </div>
            </div>

            {/* ATR Sparkline Chart */}
            <div className="atr-chart-container">
              <div className="atr-sparkline">
                <svg width="100%" height="80" viewBox="0 0 1000 80" preserveAspectRatio="none">
                  {/* Calculate chart data */}
                  {(() => {
                    const values = data.atr_history.map(p => p.atr_value);
                    const minVal = Math.min(...values);
                    const maxVal = Math.max(...values);
                    const range = maxVal - minVal || 1;

                    // Create path points
                    const points = values.map((val, idx) => {
                      const x = (idx / (values.length - 1)) * 1000;
                      const y = 70 - ((val - minVal) / range) * 60;
                      return `${x},${y}`;
                    }).join(' ');

                    return (
                      <>
                        {/* Area fill */}
                        <defs>
                          <linearGradient id="atrGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="rgba(139, 92, 246, 0.3)" />
                            <stop offset="100%" stopColor="rgba(139, 92, 246, 0.05)" />
                          </linearGradient>
                        </defs>
                        <polygon
                          points={`0,70 ${points} 1000,70`}
                          fill="url(#atrGradient)"
                        />
                        {/* Line */}
                        <polyline
                          points={points}
                          fill="none"
                          stroke="#8B5CF6"
                          strokeWidth="2"
                        />
                      </>
                    );
                  })()}
                </svg>
              </div>
              <div className="atr-chart-labels">
                <span className="chart-label-left">{new Date(data.atr_history[data.atr_history.length - 1]?.metric_time).toLocaleDateString()}</span>
                <span className="chart-label-center">ATR Trend Over Time</span>
                <span className="chart-label-right">{new Date(data.atr_history[0]?.metric_time).toLocaleDateString()}</span>
              </div>
            </div>

            {/* ATR Context Narrative */}
            <div className="narrative-section atr-narrative">
              <p className="narrative-text">
                {(() => {
                  const currentATR = data.atr_history[0]?.atr_value || 0;
                  const avgATR = data.atr_history.reduce((sum, p) => sum + (p.atr_value || 0), 0) / data.atr_history.length;
                  const diffPct = ((currentATR - avgATR) / avgATR) * 100;
                  const absDiffPct = Math.abs(diffPct);

                  return (
                    <>
                      The current ATR of <strong className="atr-highlight">${currentATR.toFixed(4)}</strong> is{' '}
                      <strong className={diffPct > 0 ? 'warning-text' : 'success-text'}>
                        {diffPct > 0 ? `${absDiffPct.toFixed(1)}% above` : `${absDiffPct.toFixed(1)}% below`}
                      </strong>{' '}
                      the {Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1}-day average of ${avgATR.toFixed(4)}.
                      {diffPct > 20 && (
                        <span className="warning-text"> This indicates significantly elevated volatility - consider reducing position sizes and widening stop losses.</span>
                      )}
                      {diffPct < -20 && (
                        <span className="success-text"> This indicates low volatility conditions - markets may be consolidating before a breakout.</span>
                      )}
                      {Math.abs(diffPct) <= 20 && (
                        <span> This represents normal volatility levels for this asset.</span>
                      )}
                    </>
                  );
                })()}
              </p>
                <p className="narrative-subtext">
                    <strong>💡 Trading Tip:</strong> Use 2-3× ATR for stop-loss placement. Current recommendation: ${(data.atr_history[0]?.atr_value * 2.5 || 0).toFixed(4)} stop distance.
                </p>
                <p className="narrative-subtext">
                    For more a more detailed view of ATR view the ATR technical indicator on the main chart.  This sparkline provides a quick overview of ATR trends over the selected date range.
                </p>
            </div>
          </div>
        </StandaloneSection>
      )}

      {/* Regime Transitions Chart */}
      {data.volatility_history && data.volatility_history.length > 0 && (
        <StandaloneSection title={`VOLATILITY REGIME OVER TIME (${data.regime_transitions.length} transitions)`}>
          <div className="regime-chart-container">
            <svg width="100%" height="120" viewBox="0 0 1000 120" preserveAspectRatio="none">
              {(() => {
                // Map regime states to numeric values for charting
                const regimeValues: Record<string, number> = {
                  'LOW': 1,
                  'MEDIUM': 2,
                  'NORMAL': 2,
                  'HIGH': 3,
                  'EXTREME': 4,
                };

                const regimeColors: Record<string, string> = {
                  'LOW': 'rgba(34, 197, 94, 0.6)',      // green
                  'MEDIUM': 'rgba(251, 146, 60, 0.6)',  // orange
                  'NORMAL': 'rgba(251, 146, 60, 0.6)',  // orange
                  'HIGH': 'rgba(239, 68, 68, 0.6)',     // red
                  'EXTREME': 'rgba(168, 85, 247, 0.6)', // purple
                };

                // Create time-series data with regime states
                const history = data.volatility_history.map(point => ({
                  time: new Date(point.analysis_time).getTime(),
                  regime: point.volatility_state || 'NORMAL',
                  value: regimeValues[point.volatility_state || 'NORMAL'] || 2,
                }));

                if (history.length === 0) return null;

                const startTime = history[history.length - 1].time;
                const endTime = history[0].time;
                const timeRange = endTime - startTime;

                // Create path segments for each regime period
                const segments: React.ReactElement[] = [];
                for (let i = 0; i < history.length - 1; i++) {
                  const current = history[i];
                  const next = history[i + 1];

                  const x1 = ((current.time - startTime) / timeRange) * 1000;
                  const x2 = ((next.time - startTime) / timeRange) * 1000;
                  const y = 100 - (current.value * 20);

                  segments.push(
                    <rect
                      key={i}
                      x={x2}
                      y={y}
                      width={x1 - x2}
                      height={100 - y}
                      fill={regimeColors[current.regime]}
                      stroke="none"
                    />
                  );
                }

                // Create step line showing regime changes
                const linePoints = history.map((point) => {
                  const x = ((point.time - startTime) / timeRange) * 1000;
                  const y = 100 - (point.value * 20);
                  return `${x},${y}`;
                }).reverse().join(' ');

                return (
                  <>
                    {segments}
                    <polyline
                      points={linePoints}
                      fill="none"
                      stroke="#e2e8f0"
                      strokeWidth="2"
                      opacity="0.8"
                    />
                  </>
                );
              })()}

              {/* Y-axis labels */}
              <text x="10" y="95" fill="#94a3b8" fontSize="10">Low</text>
              <text x="10" y="55" fill="#94a3b8" fontSize="10">Normal</text>
              <text x="10" y="35" fill="#94a3b8" fontSize="10">High</text>
              <text x="10" y="15" fill="#94a3b8" fontSize="10">Extreme</text>
            </svg>

            <div className="regime-chart-labels">
              <span className="chart-label-left">
                {data.volatility_history.length > 0 ? new Date(data.volatility_history[data.volatility_history.length - 1]?.analysis_time).toLocaleDateString() : ''}
              </span>
              <span className="chart-label-center">Volatility Regime Timeline</span>
              <span className="chart-label-right">
                {data.volatility_history.length > 0 ? new Date(data.volatility_history[0]?.analysis_time).toLocaleDateString() : ''}
              </span>
            </div>

            {/* Legend */}
            <div className="regime-legend">
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(34, 197, 94, 0.6)' }}></span>
                <span className="legend-label">Low</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(251, 146, 60, 0.6)' }}></span>
                <span className="legend-label">Normal/Medium</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(239, 68, 68, 0.6)' }}></span>
                <span className="legend-label">High</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(168, 85, 247, 0.6)' }}></span>
                <span className="legend-label">Extreme</span>
              </div>
            </div>

            {/* Transition summary */}
            {data.regime_transitions.length > 0 && (
              <div className="transition-summary">
                <p className="narrative-text">
                  <strong>{data.regime_transitions.length} regime transitions</strong> detected over the selected period.
                  Most recent: <strong>{formatTextValue(data.regime_transitions[0].from_regime)}</strong> → <strong>{formatTextValue(data.regime_transitions[0].to_regime)}</strong> at{' '}
                  {new Date(data.regime_transitions[0].timestamp).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </StandaloneSection>
      )}

      {/* Trend Over Time Chart */}
      {data.volatility_history && data.volatility_history.length > 0 && (
        <StandaloneSection title="TREND OVER TIME">
          <div className="metric-chart-container">
            <svg width="100%" height="120" viewBox="0 0 1000 120" preserveAspectRatio="none">
              {(() => {
                const trendValues: Record<string, number> = {
                  'DOWNTREND': 1,
                  'SIDEWAYS': 2,
                  'UPTREND': 3,
                };

                const trendColors: Record<string, string> = {
                  'DOWNTREND': 'rgba(239, 68, 68, 0.6)',   // red
                  'SIDEWAYS': 'rgba(251, 146, 60, 0.6)',   // orange
                  'UPTREND': 'rgba(34, 197, 94, 0.6)',     // green
                };

                const history = data.volatility_history.map(point => ({
                  time: new Date(point.analysis_time).getTime(),
                  state: point.trend || 'SIDEWAYS',
                  value: trendValues[point.trend || 'SIDEWAYS'] || 2,
                }));

                if (history.length === 0) return null;

                const startTime = history[history.length - 1].time;
                const endTime = history[0].time;
                const timeRange = endTime - startTime;

                const segments: React.ReactElement[] = [];
                for (let i = 0; i < history.length - 1; i++) {
                  const current = history[i];
                  const next = history[i + 1];

                  const x1 = ((current.time - startTime) / timeRange) * 1000;
                  const x2 = ((next.time - startTime) / timeRange) * 1000;
                  const y = 90 - (current.value * 25);

                  segments.push(
                    <rect
                      key={i}
                      x={x2}
                      y={y}
                      width={x1 - x2}
                      height={90 - y}
                      fill={trendColors[current.state]}
                      stroke="none"
                    />
                  );
                }

                const linePoints = history.map((point) => {
                  const x = ((point.time - startTime) / timeRange) * 1000;
                  const y = 90 - (point.value * 25);
                  return `${x},${y}`;
                }).reverse().join(' ');

                return (
                  <>
                    {segments}
                    <polyline
                      points={linePoints}
                      fill="none"
                      stroke="#e2e8f0"
                      strokeWidth="2"
                      opacity="0.8"
                    />
                    <text x="10" y="85" fill="#94a3b8" fontSize="10">Downtrend</text>
                    <text x="10" y="55" fill="#94a3b8" fontSize="10">Sideways</text>
                    <text x="10" y="25" fill="#94a3b8" fontSize="10">Uptrend</text>
                  </>
                );
              })()}
            </svg>
            <div className="metric-chart-labels">
              <span className="chart-label-left">
                {new Date(data.volatility_history[data.volatility_history.length - 1]?.analysis_time).toLocaleDateString()}
              </span>
              <span className="chart-label-center">Trend Direction Timeline</span>
              <span className="chart-label-right">
                {new Date(data.volatility_history[0]?.analysis_time).toLocaleDateString()}
              </span>
            </div>
            <div className="metric-legend">
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(239, 68, 68, 0.6)' }}></span>
                <span className="legend-label">Downtrend</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(251, 146, 60, 0.6)' }}></span>
                <span className="legend-label">Sideways</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(34, 197, 94, 0.6)' }}></span>
                <span className="legend-label">Uptrend</span>
              </div>
            </div>
          </div>
        </StandaloneSection>
      )}

      {/* Momentum Over Time Chart */}
      {data.volatility_history && data.volatility_history.length > 0 && (
        <StandaloneSection title="MOMENTUM OVER TIME">
          <div className="metric-chart-container">
            <svg width="100%" height="120" viewBox="0 0 1000 120" preserveAspectRatio="none">
              {(() => {
                const momentumValues: Record<string, number> = {
                  'BEARISH': 1,
                  'NEUTRAL': 2,
                  'BULLISH': 3,
                };

                const momentumColors: Record<string, string> = {
                  'BEARISH': 'rgba(239, 68, 68, 0.6)',     // red
                  'NEUTRAL': 'rgba(148, 163, 184, 0.6)',   // gray
                  'BULLISH': 'rgba(34, 197, 94, 0.6)',     // green
                };

                const history = data.volatility_history.map(point => ({
                  time: new Date(point.analysis_time).getTime(),
                  state: point.momentum_direction || 'NEUTRAL',
                  value: momentumValues[point.momentum_direction || 'NEUTRAL'] || 2,
                }));

                if (history.length === 0) return null;

                const startTime = history[history.length - 1].time;
                const endTime = history[0].time;
                const timeRange = endTime - startTime;

                const segments: React.ReactElement[] = [];
                for (let i = 0; i < history.length - 1; i++) {
                  const current = history[i];
                  const next = history[i + 1];

                  const x1 = ((current.time - startTime) / timeRange) * 1000;
                  const x2 = ((next.time - startTime) / timeRange) * 1000;
                  const y = 90 - (current.value * 25);

                  segments.push(
                    <rect
                      key={i}
                      x={x2}
                      y={y}
                      width={x1 - x2}
                      height={90 - y}
                      fill={momentumColors[current.state]}
                      stroke="none"
                    />
                  );
                }

                const linePoints = history.map((point) => {
                  const x = ((point.time - startTime) / timeRange) * 1000;
                  const y = 90 - (point.value * 25);
                  return `${x},${y}`;
                }).reverse().join(' ');

                return (
                  <>
                    {segments}
                    <polyline
                      points={linePoints}
                      fill="none"
                      stroke="#e2e8f0"
                      strokeWidth="2"
                      opacity="0.8"
                    />
                    <text x="10" y="85" fill="#94a3b8" fontSize="10">Bearish</text>
                    <text x="10" y="55" fill="#94a3b8" fontSize="10">Neutral</text>
                    <text x="10" y="25" fill="#94a3b8" fontSize="10">Bullish</text>
                  </>
                );
              })()}
            </svg>
            <div className="metric-chart-labels">
              <span className="chart-label-left">
                {new Date(data.volatility_history[data.volatility_history.length - 1]?.analysis_time).toLocaleDateString()}
              </span>
              <span className="chart-label-center">Momentum Direction Timeline</span>
              <span className="chart-label-right">
                {new Date(data.volatility_history[0]?.analysis_time).toLocaleDateString()}
              </span>
            </div>
            <div className="metric-legend">
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(239, 68, 68, 0.6)' }}></span>
                <span className="legend-label">Bearish</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(148, 163, 184, 0.6)' }}></span>
                <span className="legend-label">Neutral</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(34, 197, 94, 0.6)' }}></span>
                <span className="legend-label">Bullish</span>
              </div>
            </div>
          </div>
        </StandaloneSection>
      )}

      {/* Market Regime Over Time Chart */}
      {data.volatility_history && data.volatility_history.length > 0 && (
        <StandaloneSection title="MARKET REGIME OVER TIME">
          <div className="metric-chart-container">
            <svg width="100%" height="150" viewBox="0 0 1000 150" preserveAspectRatio="none">
              {(() => {
                const regimeValues: Record<string, number> = {
                  'STRONG_DOWNTREND': 1,
                  'DOWNTREND_MOMENTUM': 2,
                  'RANGING': 3,
                  'CONSOLIDATION': 3,
                  'REVERSAL_POTENTIAL': 4,
                  'UPTREND_MOMENTUM': 5,
                  'STRONG_UPTREND': 6,
                };

                const regimeColors: Record<string, string> = {
                  'STRONG_DOWNTREND': 'rgba(185, 28, 28, 0.6)',    // dark red
                  'DOWNTREND_MOMENTUM': 'rgba(239, 68, 68, 0.6)',  // red
                  'RANGING': 'rgba(148, 163, 184, 0.6)',           // gray
                  'CONSOLIDATION': 'rgba(148, 163, 184, 0.6)',     // gray
                  'REVERSAL_POTENTIAL': 'rgba(251, 146, 60, 0.6)', // orange
                  'UPTREND_MOMENTUM': 'rgba(34, 197, 94, 0.6)',    // green
                  'STRONG_UPTREND': 'rgba(21, 128, 61, 0.6)',      // dark green
                };

                const history = data.volatility_history.map(point => ({
                  time: new Date(point.analysis_time).getTime(),
                  state: point.market_regime || 'RANGING',
                  value: regimeValues[point.market_regime || 'RANGING'] || 3,
                }));

                if (history.length === 0) return null;

                const startTime = history[history.length - 1].time;
                const endTime = history[0].time;
                const timeRange = endTime - startTime;

                const segments: React.ReactElement[] = [];
                for (let i = 0; i < history.length - 1; i++) {
                  const current = history[i];
                  const next = history[i + 1];

                  const x1 = ((current.time - startTime) / timeRange) * 1000;
                  const x2 = ((next.time - startTime) / timeRange) * 1000;
                  const y = 130 - (current.value * 20);

                  segments.push(
                    <rect
                      key={i}
                      x={x2}
                      y={y}
                      width={x1 - x2}
                      height={130 - y}
                      fill={regimeColors[current.state] || 'rgba(148, 163, 184, 0.6)'}
                      stroke="none"
                    />
                  );
                }

                const linePoints = history.map((point) => {
                  const x = ((point.time - startTime) / timeRange) * 1000;
                  const y = 130 - (point.value * 20);
                  return `${x},${y}`;
                }).reverse().join(' ');

                return (
                  <>
                    {segments}
                    <polyline
                      points={linePoints}
                      fill="none"
                      stroke="#e2e8f0"
                      strokeWidth="2"
                      opacity="0.8"
                    />
                    <text x="5" y="135" fill="#94a3b8" fontSize="9">Strong Down</text>
                    <text x="5" y="115" fill="#94a3b8" fontSize="9">Down</text>
                    <text x="5" y="95" fill="#94a3b8" fontSize="9">Ranging</text>
                    <text x="5" y="75" fill="#94a3b8" fontSize="9">Reversal</text>
                    <text x="5" y="55" fill="#94a3b8" fontSize="9">Up</text>
                    <text x="5" y="35" fill="#94a3b8" fontSize="9">Strong Up</text>
                  </>
                );
              })()}
            </svg>
            <div className="metric-chart-labels">
              <span className="chart-label-left">
                {new Date(data.volatility_history[data.volatility_history.length - 1]?.analysis_time).toLocaleDateString()}
              </span>
              <span className="chart-label-center">Market Regime Classification Timeline</span>
              <span className="chart-label-right">
                {new Date(data.volatility_history[0]?.analysis_time).toLocaleDateString()}
              </span>
            </div>
            <div className="metric-legend regime-legend-extended">
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(185, 28, 28, 0.6)' }}></span>
                <span className="legend-label">Strong Downtrend</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(239, 68, 68, 0.6)' }}></span>
                <span className="legend-label">Downtrend</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(148, 163, 184, 0.6)' }}></span>
                <span className="legend-label">Ranging</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(251, 146, 60, 0.6)' }}></span>
                <span className="legend-label">Reversal</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(34, 197, 94, 0.6)' }}></span>
                <span className="legend-label">Uptrend</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: 'rgba(21, 128, 61, 0.6)' }}></span>
                <span className="legend-label">Strong Uptrend</span>
              </div>
            </div>
          </div>
        </StandaloneSection>
      )}
      </>
      )}
    </div>
  );
};

export default VolatilityDetailPanel;

